import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { DownloadSimple, Sparkle, FilePdf, Image as ImageIcon, FileText } from "@phosphor-icons/react";
import { toast } from "sonner";

// Format options
const FORMATS = [
  { id: "original", label: "Original" },
  { id: "pdf",      label: "PDF" },
  { id: "jpg",      label: "JPG" },
  { id: "png",      label: "PNG" },
  { id: "webp",     label: "WEBP" },
];

// Target size options in KB (0 = original)
const SIZE_OPTIONS = [
  { kb: 0,    label: "Original" },
  { kb: 20,   label: "20 KB" },
  { kb: 50,   label: "50 KB" },
  { kb: 100,  label: "100 KB" },
  { kb: 150,  label: "150 KB" },
  { kb: 200,  label: "200 KB" },
  { kb: 300,  label: "300 KB" },
  { kb: 500,  label: "500 KB" },
  { kb: 1024, label: "1 MB" },
  { kb: 2048, label: "2 MB" },
];

const QUALITIES = [
  { id: "high",   label: "High" },
  { id: "medium", label: "Medium" },
  { id: "low",    label: "Low" },
];

const PRESET_COLORS = {
  pan:         "bg-slate-100 text-slate-800",
  aadhaar:     "bg-blue-50 text-blue-800",
  passport:    "bg-red-50 text-red-800",
  mahaonline:  "bg-orange-50 text-orange-800",
  aaplesarkar: "bg-emerald-50 text-emerald-800",
  csc:         "bg-purple-50 text-purple-800",
};

function fmtBytes(b) {
  if (!b && b !== 0) return "—";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(2)} MB`;
}

export default function DownloadDialog({ doc, open, onOpenChange }) {
  const [format, setFormat]   = useState("original");
  const [targetKb, setTargetKb] = useState(0);
  const [customKb, setCustomKb] = useState("");
  const [quality, setQuality] = useState("high");
  const [preset, setPreset]   = useState("");
  const [presets, setPresets] = useState([]);
  const [estimating, setEstimating] = useState(false);
  const [estimate, setEstimate] = useState(null); // { original_bytes, output_bytes, achieved_target }
  const [downloading, setDownloading] = useState(false);

  // Reset each time dialog opens
  useEffect(() => {
    if (open) {
      setFormat("original"); setTargetKb(0); setCustomKb(""); setQuality("high");
      setPreset(""); setEstimate(null);
      if (presets.length === 0) api.get("/documents/presets").then((r) => setPresets(r.data)).catch(() => {});
    }
  }, [open]);

  // Re-estimate whenever knobs change
  useEffect(() => {
    if (!open || !doc) return;
    const t = setTimeout(() => runEstimate(), 250);
    return () => clearTimeout(t);
  }, [format, targetKb, customKb, quality, preset, open, doc]);

  const effectiveTargetKb = preset
    ? (presets.find((p) => p.id === preset)?.target_kb || 0)
    : (targetKb === -1 ? Math.max(0, parseInt(customKb || "0", 10)) : targetKb);

  const runEstimate = async () => {
    if (!doc?.id) return;
    setEstimating(true);
    try {
      const { data } = await api.get(`/documents/${doc.id}/estimate`, {
        params: { format, target_kb: effectiveTargetKb, quality, preset: preset || undefined },
      });
      setEstimate(data);
    } catch (e) {
      setEstimate(null);
    } finally {
      setEstimating(false);
    }
  };

  const doDownload = async () => {
    if (!doc?.id) return;
    setDownloading(true);
    try {
      const token = localStorage.getItem("csc_token");
      const params = new URLSearchParams();
      params.set("format", format);
      params.set("target_kb", String(effectiveTargetKb));
      params.set("quality", quality);
      if (preset) params.set("preset", preset);
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/documents/${doc.id}/download?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) {
        const t = await res.text();
        throw new Error(t || "Download failed");
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      // Server sets Content-Disposition; extract if possible
      const cd = res.headers.get("Content-Disposition") || "";
      const m = cd.match(/filename="([^"]+)"/);
      a.href = url;
      a.download = m?.[1] || (doc.file_name || "download");
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded (${fmtBytes(blob.size)})`);
      onOpenChange(false);
    } catch (e) {
      toast.error(String(e.message || e).slice(0, 160));
    } finally {
      setDownloading(false);
    }
  };

  const original = estimate?.original_bytes;
  const output   = estimate?.output_bytes;
  const targetBytes = effectiveTargetKb ? effectiveTargetKb * 1024 : 0;
  const achieved = estimate?.achieved_target !== false;
  const compressionRatio = original && output ? Math.round((1 - output / original) * 100) : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-white max-h-[90vh] overflow-y-auto" data-testid="download-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "var(--font-heading)" }} className="flex items-center gap-2">
            <DownloadSimple size={20} weight="duotone" className="text-orange-600" />
            Download {doc?.doc_type || "Document"}
          </DialogTitle>
        </DialogHeader>

        {/* Government Portal Presets — one-tap */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Sparkle size={11} weight="fill" className="text-orange-500" />
            <Label className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-600">Government Portal Presets</Label>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="preset-row">
            {presets.map((p) => (
              <button
                key={p.id}
                data-testid={`preset-${p.id}`}
                onClick={() => setPreset((cur) => cur === p.id ? "" : p.id)}
                className={`px-3 py-1.5 rounded-md border text-[11px] font-medium transition-all ${preset === p.id ? "ring-2 ring-orange-500 border-orange-500 " + (PRESET_COLORS[p.id] || "bg-slate-100 text-slate-800") : "border-slate-200 text-slate-700 hover:border-slate-300 bg-white"}`}
              >
                <span className="font-semibold">{p.label}</span>
                <span className="ml-1.5 text-slate-500">{p.format.toUpperCase()} · {p.target_kb < 1024 ? `${p.target_kb} KB` : `${p.target_kb / 1024} MB`}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="border-t border-slate-100 my-2" />

        {/* Format */}
        <FieldSection title="Format">
          <div className="flex flex-wrap gap-2" data-testid="format-row">
            {FORMATS.map((f) => (
              <button
                key={f.id}
                onClick={() => { setPreset(""); setFormat(f.id); }}
                data-testid={`format-${f.id}`}
                disabled={!!preset}
                className={`px-3 py-1.5 rounded-md border text-xs font-medium transition-colors ${!preset && format === f.id ? "border-orange-500 bg-orange-50 text-orange-800" : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"} ${preset ? "opacity-40 cursor-not-allowed" : ""}`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </FieldSection>

        {/* Target size */}
        <FieldSection title="File Size">
          <div className="flex flex-wrap gap-2 mb-2" data-testid="size-row">
            {SIZE_OPTIONS.map((s) => (
              <button
                key={s.kb}
                onClick={() => { setPreset(""); setTargetKb(s.kb); }}
                data-testid={`size-${s.kb}`}
                disabled={!!preset}
                className={`px-3 py-1.5 rounded-md border text-xs font-medium transition-colors ${!preset && targetKb === s.kb ? "border-orange-500 bg-orange-50 text-orange-800" : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"} ${preset ? "opacity-40 cursor-not-allowed" : ""}`}
              >
                {s.label}
              </button>
            ))}
            <button
              onClick={() => { setPreset(""); setTargetKb(-1); }}
              data-testid="size-custom"
              disabled={!!preset}
              className={`px-3 py-1.5 rounded-md border text-xs font-medium transition-colors ${!preset && targetKb === -1 ? "border-orange-500 bg-orange-50 text-orange-800" : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"} ${preset ? "opacity-40 cursor-not-allowed" : ""}`}
            >
              Custom
            </button>
          </div>
          {!preset && targetKb === -1 && (
            <div className="flex items-center gap-2">
              <Input type="number" min={5} max={51200} value={customKb} onChange={(e) => setCustomKb(e.target.value)}
                placeholder="Enter KB" className="w-32 h-9" data-testid="size-custom-input" />
              <span className="text-xs text-slate-500">KB</span>
            </div>
          )}
        </FieldSection>

        {/* Quality */}
        <FieldSection title="Quality">
          <div className="flex flex-wrap gap-2">
            {QUALITIES.map((q) => (
              <button
                key={q.id}
                onClick={() => { setPreset(""); setQuality(q.id); }}
                data-testid={`quality-${q.id}`}
                disabled={!!preset}
                className={`px-3 py-1.5 rounded-md border text-xs font-medium transition-colors ${!preset && quality === q.id ? "border-orange-500 bg-orange-50 text-orange-800" : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"} ${preset ? "opacity-40 cursor-not-allowed" : ""}`}
              >
                {q.label}
              </button>
            ))}
          </div>
        </FieldSection>

        {/* Preview */}
        <div className="rounded-md border border-slate-200 bg-slate-50 p-4 mt-2" data-testid="download-preview">
          <div className="grid grid-cols-3 gap-3 text-center">
            <PreviewCell label="Original" value={fmtBytes(original)} tone="text-slate-800" />
            <PreviewCell
              label={targetBytes ? "Target" : "No target"}
              value={targetBytes ? fmtBytes(targetBytes) : "—"}
              tone="text-slate-500"
            />
            <PreviewCell
              label="Estimated Output"
              value={estimating ? "…" : fmtBytes(output)}
              tone={achieved ? "text-emerald-700" : "text-amber-700"}
              testid="estimated-output"
            />
          </div>
          {output != null && original != null && compressionRatio > 0 && (
            <div className="text-center mt-3 text-xs text-emerald-700">
              {compressionRatio}% smaller · {achieved ? "✓ Ready to download" : "⚠ Could not hit exact target — best effort"}
            </div>
          )}
        </div>

        <DialogFooter className="pt-3">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="download-cancel-btn">Cancel</Button>
          <Button onClick={doDownload} disabled={downloading || estimating}
            className="bg-orange-600 hover:bg-orange-700 text-white gap-2" data-testid="download-confirm-btn">
            <DownloadSimple size={15} weight="bold" />
            {downloading ? "Downloading…" : "Download"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FieldSection({ title, children }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-600 block mb-2">{title}</Label>
      {children}
    </div>
  );
}

function PreviewCell({ label, value, tone, testid }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`text-lg font-bold mt-1 ${tone}`} data-testid={testid} style={{ fontFamily: "var(--font-heading)" }}>{value}</div>
    </div>
  );
}
